import React from 'react';
import { CheckCircle2, XCircle, Search, HelpCircle } from 'lucide-react';
import CTASection from '../../components/CTASection';
import ChaosToOrder from '../../components/ChaosToOrder';
import SEO from '../../components/SEO';
import GradientOverlay from '../../components/GradientOverlay';
import LogomarkPattern from '../../components/LogomarkPattern';

const LeadGeneration = () => {
  const useCases = [
    {
      title: 'Marine Engineering Firm',
      location: 'Gold Coast',
      description: 'Targeting superyacht maintenance contracts by identifying and engaging with yacht captains and fleet managers—a highly specific niche market that traditional advertising can\'t reach efficiently.'
    },
    {
      title: 'Compliance Software Company',
      location: 'Byron Bay',
      description: 'Generates qualified leads from mid-sized law firms across Queensland by identifying firms with specific compliance pain points and recent regulatory changes.'
    },
    {
      title: 'Niche Construction Supplier',
      location: 'Indonesia',
      description: 'Connects with architects and builders working on high-end residential projects by analysing permit data, project announcements, and professional networks.'
    }
  ];

  const costsOfInaction = [
    'Wasted advertising budget on broad, untargeted campaigns',
    'Sales team bogged down with unqualified, low-intent leads',
    'Low conversion rates eating into profitability',
    'Slow, unpredictable growth limiting expansion plans',
    'Missing out on ideal customers while competitors snap them up'
  ];

  const faqs = [
    {
      question: 'How does AI find better leads than traditional methods?',
      answer: 'AI analyses thousands of data points—like company size, industry, technology usage, hiring trends, and online activity—to build a precise profile of your ideal customer. It then identifies companies that match this profile with much higher accuracy than manual searching, ensuring we only engage with high-potential prospects.'
    },
    {
      question: 'Is AI lead generation expensive?',
      answer: 'It\'s more cost-effective. By focusing only on high-quality, qualified leads, you eliminate wasted spend on advertising to the wrong audience. The ROI is significantly higher because your sales team spends their time closing deals, not chasing dead ends.'
    },
    {
      question: 'Will AI replace my sales team?',
      answer: 'No, it empowers them. AI handles the time-consuming research and qualification process, freeing up your expert salespeople to do what they do best: build relationships and close deals with prospects who are already a great fit.'
    },
    {
      question: 'How do you ensure leads are relevant to my niche B2B market?',
      answer: 'Our process is built around your niche. We start by deeply understanding your business and ideal client. The AI is then configured with specific geographic parameters and industry-specific data points to ensure every lead is locally or regionally relevant and fits your unique market.'
    }
  ];

  return (
    <div style={{ marginTop: '80px' }}>
      <SEO 
        title="B2B Lead Generation Gold Coast"
        description="AI-powered B2B lead generation for Gold Coast and Australian businesses. Find and engage your ideal customers with precision targeting."
        keywords="B2B Lead Generation Gold Coast, Lead Generation Australia, AI Lead Generation Brisbane, Sales Leads Queensland, Business Development Gold Coast, Lead Gen Services Australia"
        canonical="https://resolvdigital.com.au/services/lead-generation"
      />
      
      {/* Hero Section - DARK with Background Image */}
      <section style={{
        minHeight: '70vh',
        display: 'flex',
        alignItems: 'center',
        backgroundColor: '#0a4447',
        backgroundImage: 'url(https://images.unsplash.com/photo-1515169067868-5387ec356754)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        position: 'relative',
        padding: '120px 40px 80px'
      }}>
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(10, 68, 71, 0.9)'
        }} />
        <div className="container" style={{
          maxWidth: '1400px',
          margin: '0 auto',
          position: 'relative',
          zIndex: 2
        }}>
          <div style={{ maxWidth: '900px' }}>
            <div style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '12px',
              backgroundColor: 'rgba(13, 115, 119, 0.2)',
              border: '1px solid #0D7377',
              borderRadius: '10rem',
              padding: '8px 20px',
              marginBottom: '24px'
            }}>
              <Search size={20} style={{ color: '#0D7377' }} />
              <span style={{
                fontFamily: 'Lato, sans-serif',
                fontSize: '0.875rem',
                fontWeight: 600,
                color: '#0D7377',
                textTransform: 'uppercase',
                letterSpacing: '0.05em'
              }}>
                Service
              </span>
            </div>
            <h1 style={{
              fontFamily: 'Montserrat, sans-serif',
              fontWeight: 900,
              fontSize: 'clamp(3rem, 6vw, 5rem)',
              lineHeight: 1.1,
              color: '#C9A227',
              textTransform: 'uppercase',
              marginBottom: '32px'
            }}>
              Resolve Your Pipeline Problem.<br />Get Qualified B2B Leads.
            </h1>
            <p style={{
              fontFamily: 'Lato, sans-serif',
              fontSize: '1.5rem',
              lineHeight: 1.6,
              color: '#ffffff'
            }}>
              Specialist B2B lead generation for niche Australian businesses. Stop wasting time on bad leads. Get high-quality, qualified prospects delivered to your pipeline.
            </p>
          </div>
        </div>
      </section>

      {/* What is Smart Lead Generation - LIGHT TEAL with Chaos Animation */}
      <section style={{
        padding: '120px 40px',
        backgroundColor: '#e8f4f5',
        position: 'relative',
        overflow: 'hidden'
      }}>
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 0
        }}>
          <ChaosToOrder showLabels={false} opacity={0.3} />
        </div>
        
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'linear-gradient(135deg, rgba(232, 244, 245, 0.4) 0%, rgba(255, 255, 255, 0.5) 50%, rgba(232, 244, 245, 0.3) 100%)',
          zIndex: 1,
          pointerEvents: 'none'
        }} />
        
        <div className="container" style={{
          maxWidth: '1200px',
          margin: '0 auto',
          position: 'relative',
          zIndex: 2
        }}>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 400px), 1fr))',
            gap: '40px',
            alignItems: 'center'
          }}>
            <div>
              <h2 style={{
                fontFamily: 'Montserrat, sans-serif',
                fontWeight: 900,
                fontSize: 'clamp(2rem, 4vw, 3rem)',
                lineHeight: 1.2,
                background: 'linear-gradient(135deg, #0a4447 0%, #C9A227 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
                textTransform: 'uppercase',
                marginBottom: '32px'
              }}>
                What is Smart Lead Generation?
              </h2>
              <div style={{
                fontFamily: 'Lato, sans-serif',
                fontSize: '1.125rem',
                lineHeight: 1.8,
                color: '#0a4447'
              }}>
                <p style={{ marginBottom: '20px' }}>
                  This is a data-first approach to B2B lead generation. Instead of casting a wide, expensive net, we use AI and analytics to identify and engage ideal customer profiles for niche businesses.
                </p>
                <p style={{ marginBottom: '20px' }}>
                  We analyse firmographic data, technology stacks, buying signals, and market trends to find prospects who are not just a fit—but are actively experiencing the problems your solution solves.
                </p>
                <p style={{ fontWeight: 600, color: '#C9A227' }}>
                  Quality over quantity. Every time.
                </p>
              </div>
            </div>
            <div style={{
              backgroundColor: '#ffffff',
              border: '2px solid #0D7377',
              borderRadius: '8px',
              padding: '48px',
              boxShadow: '0 8px 32px rgba(13, 115, 119, 0.1)'
            }}>
              <h3 style={{
                fontFamily: 'Montserrat, sans-serif',
                fontWeight: 700,
                fontSize: '1.5rem',
                color: '#0a4447',
                marginBottom: '24px',
                textTransform: 'uppercase'
              }}>
                What You Get
              </h3>
              <ul style={{
                listStyle: 'none',
                padding: 0,
                margin: 0,
                display: 'flex',
                flexDirection: 'column',
                gap: '16px'
              }}>
                {[
                  'Pre-qualified, high-intent prospects',
                  'Detailed company & contact intelligence',
                  'Geo-targeted lead generation',
                  'Niche industry specialisation',
                  'Measurable ROI tracking'
                ].map((item, index) => (
                  <li key={index} style={{
                    display: 'flex',
                    gap: '12px',
                    alignItems: 'flex-start',
                    fontFamily: 'Lato, sans-serif',
                    fontSize: '1rem',
                    color: '#0a4447'
                  }}>
                    <CheckCircle2 size={24} style={{
                      color: '#0D7377',
                      flexShrink: 0,
                      marginTop: '2px'
                    }} />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Why It's Relevant - WHITE with Logomark Pattern */}
      <section style={{
        padding: '120px 40px',
        backgroundColor: '#ffffff',
        position: 'relative',
        overflow: 'hidden'
      }}>
        <LogomarkPattern opacity={0.025} density="medium" tint="teal" />
        <GradientOverlay variant="subtle" opacity={0.1} />
        
        <div className="container" style={{
          maxWidth: '1200px',
          margin: '0 auto',
          textAlign: 'center',
          position: 'relative',
          zIndex: 2
        }}>
          <h2 style={{
            fontFamily: 'Montserrat, sans-serif',
            fontWeight: 900,
            fontSize: 'clamp(2.5rem, 5vw, 4rem)',
            lineHeight: 1.1,
            background: 'linear-gradient(135deg, #0a4447 0%, #C9A227 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
            textTransform: 'uppercase',
            marginBottom: '32px'
          }}>
            Why Niche B2B Businesses Need This
          </h2>
          <p style={{
            fontFamily: 'Lato, sans-serif',
            fontSize: '1.25rem',
            lineHeight: 1.7,
            color: '#0a4447',
            maxWidth: '900px',
            margin: '0 auto 48px'
          }}>
            Niche B2B businesses can't afford to waste marketing spend on broad campaigns. Our targeted approach finds the specific clients that need your specialized services, delivering a higher ROI and faster growth regardless of location.
          </p>
        </div>
      </section>

      {/* Cost of Inaction - LIGHT TEAL */}
      <section style={{
        padding: '120px 40px',
        backgroundColor: '#e8f4f5',
        position: 'relative',
        overflow: 'hidden'
      }}>
        <LogomarkPattern opacity={0.02} density="sparse" tint="gold" />
        
        <div className="container" style={{
          maxWidth: '1200px',
          margin: '0 auto',
          position: 'relative',
          zIndex: 2
        }}>
          <h2 style={{
            fontFamily: 'Montserrat, sans-serif',
            fontWeight: 900,
            fontSize: 'clamp(2.5rem, 5vw, 4rem)',
            lineHeight: 1.1,
            background: 'linear-gradient(135deg, #0a4447 0%, #C9A227 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
            textTransform: 'uppercase',
            marginBottom: '48px',
            textAlign: 'center'
          }}>
            The Cost of Untargeted Marketing
          </h2>
          <div style={{
            backgroundColor: '#ffffff',
            border: '2px solid rgba(220, 53, 69, 0.3)',
            borderRadius: '8px',
            padding: '48px',
            maxWidth: '900px',
            margin: '0 auto',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.08)'
          }}>
            <ul style={{
              listStyle: 'none',
              padding: 0,
              margin: 0,
              display: 'flex',
              flexDirection: 'column',
              gap: '20px'
            }}>
              {costsOfInaction.map((cost, index) => (
                <li key={index} style={{
                  display: 'flex',
                  gap: '16px',
                  alignItems: 'flex-start',
                  fontFamily: 'Lato, sans-serif',
                  fontSize: '1.125rem',
                  lineHeight: 1.6,
                  color: '#1a1c1b'
                }}>
                  <XCircle size={24} style={{
                    color: '#dc3545',
                    flexShrink: 0,
                    marginTop: '4px'
                  }} />
                  <span>{cost}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Use Cases - DARK (contrast section) */}
      <section style={{
        padding: '120px 40px',
        backgroundColor: '#084144',
        position: 'relative',
        overflow: 'hidden'
      }}>
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 0
        }}>
          <ChaosToOrder showLabels={false} opacity={0.1} />
        </div>
        <div className="container" style={{
          maxWidth: '1400px',
          margin: '0 auto',
          position: 'relative',
          zIndex: 2
        }}>
          <h2 style={{
            fontFamily: 'Montserrat, sans-serif',
            fontWeight: 900,
            fontSize: 'clamp(2.5rem, 5vw, 4rem)',
            lineHeight: 1.1,
            color: '#C9A227',
            textTransform: 'uppercase',
            marginBottom: '24px',
            textAlign: 'center'
          }}>
            Real Australian B2B Success Stories
          </h2>
          <p style={{
            fontFamily: 'Lato, sans-serif',
            fontSize: '1.25rem',
            lineHeight: 1.6,
            color: '#b8d8da',
            maxWidth: '800px',
            margin: '0 auto 80px',
            textAlign: 'center'
          }}>
            See how targeted lead generation is transforming sales pipelines for niche businesses.
          </p>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 350px), 1fr))',
            gap: '40px'
          }}>
            {useCases.map((useCase, index) => (
              <div key={index} style={{
                backgroundColor: 'rgba(10, 93, 96, 0.6)',
                backdropFilter: 'blur(10px)',
                border: '1px solid rgba(201, 162, 39, 0.3)',
                borderRadius: '8px',
                padding: '40px',
                transition: 'all 0.3s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = '#C9A227';
                e.currentTarget.style.transform = 'translateY(-8px)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = 'rgba(201, 162, 39, 0.3)';
                e.currentTarget.style.transform = 'translateY(0)';
              }}
              >
                <div style={{
                  display: 'inline-block',
                  backgroundColor: '#0D7377',
                  color: '#ffffff',
                  padding: '6px 16px',
                  borderRadius: '4px',
                  fontFamily: 'Lato, sans-serif',
                  fontSize: '0.875rem',
                  fontWeight: 600,
                  marginBottom: '20px',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em'
                }}>
                  {useCase.location}
                </div>
                <h3 style={{
                  fontFamily: 'Montserrat, sans-serif',
                  fontWeight: 700,
                  fontSize: '1.5rem',
                  color: '#C9A227',
                  marginBottom: '16px'
                }}>
                  {useCase.title}
                </h3>
                <p style={{
                  fontFamily: 'Lato, sans-serif',
                  fontSize: '1.125rem',
                  lineHeight: 1.7,
                  color: '#ffffff'
                }}>
                  {useCase.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQs - WHITE with Logomark Pattern */}
      <section style={{
        padding: '120px 40px',
        backgroundColor: '#ffffff',
        position: 'relative',
        overflow: 'hidden'
      }}>
        <LogomarkPattern opacity={0.02} density="sparse" tint="teal" />
        
        <div className="container" style={{
          maxWidth: '1000px',
          margin: '0 auto',
          position: 'relative',
          zIndex: 2
        }}>
          <h2 style={{
            fontFamily: 'Montserrat, sans-serif',
            fontWeight: 900,
            fontSize: 'clamp(2.5rem, 5vw, 4rem)',
            lineHeight: 1.1,
            background: 'linear-gradient(135deg, #0a4447 0%, #C9A227 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
            textTransform: 'uppercase',
            marginBottom: '64px',
            textAlign: 'center'
          }}>
            AI-Powered Lead Generation FAQs
          </h2>

          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '32px'
          }}>
            {faqs.map((faq, index) => (
              <div key={index} style={{
                backgroundColor: '#e8f4f5',
                border: '1px solid rgba(13, 115, 119, 0.2)',
                borderRadius: '8px',
                padding: '40px',
                boxShadow: '0 4px 20px rgba(0, 0, 0, 0.06)'
              }}>
                <div style={{
                  display: 'flex',
                  gap: '16px',
                  alignItems: 'flex-start',
                  marginBottom: '16px'
                }}>
                  <HelpCircle size={24} style={{
                    color: '#0D7377',
                    flexShrink: 0,
                    marginTop: '4px'
                  }} />
                  <h3 style={{
                    fontFamily: 'Montserrat, sans-serif',
                    fontWeight: 700,
                    fontSize: '1.25rem',
                    color: '#0a4447',
                    margin: 0
                  }}>
                    {faq.question}
                  </h3>
                </div>
                <p style={{
                  fontFamily: 'Lato, sans-serif',
                  fontSize: '1.0625rem',
                  lineHeight: 1.7,
                  color: '#4a5568',
                  marginLeft: '40px'
                }}>
                  {faq.answer}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <CTASection />
    </div>
  );
};

export default LeadGeneration;
